import { AxiosResponse } from 'axios';
import { FilterBlock } from 'components/FiltersBlock';
import { Tabs } from 'components/redesign/Tabs';
import { useAppDispatch } from 'hooks/useAppDispatch';
import { useAppSelector } from 'hooks/useAppSelector';
import { Fragment, useEffect, useState } from 'react';
import { useQueries } from 'react-query';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { modalActions } from 'reduxStore/reducers/ModalSlice';
import { AgentService } from 'services/AgentService';
import { GetClientOrders } from 'services/AgentService/types';
import { PageWrapper } from 'ui/redesign/PageWrapper';
import { Table } from 'ui/redesign/Table';
import { TableCard } from 'ui/redesign/Table/TableCard';
import { TableLoading } from 'ui/redesign/Table/TableLoading';
import { Spinner } from 'ui/Spinner';
import { CustomerRepresentativeOrderTabsEnum } from './types';
import { getInitialCustomerRepresentativeFilters } from 'reduxStore/reducers/OrdersSlice/selectors/getCustomerRepresentativeFilters';
import { CustomerRepresentativeFiltersForm } from './CustomerRepresentativeFiltersForm';
import { BalanceInformer } from './BalanceInformer';
import { customerRepresentativeTabs, customerRepresentativeWorkTableHeadings } from './consts';
import { ordersActions } from 'reduxStore/reducers/OrdersSlice';
import { determinateCustomerRepresentativeFiltersCount } from 'pages/Orders/utils';
import { NoneOrders } from 'pages/Orders/NoneOrders';
import { IconApplication } from 'assets/icons';

export const CustomerRepresentativeOrdersPage = () => {
  const customerRepresentativeOrderTypes = Object.values(CustomerRepresentativeOrderTabsEnum);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const { tab = CustomerRepresentativeOrderTabsEnum.awaiting } = useParams<{
    tab: CustomerRepresentativeOrderTabsEnum;
  }>();
  const [ordersCount, setOrdersCount] = useState<
    Record<CustomerRepresentativeOrderTabsEnum, number>
  >({} as Record<CustomerRepresentativeOrderTabsEnum, number>);
  const filters = useAppSelector(getInitialCustomerRepresentativeFilters);

  const queryResults = useQueries(
    customerRepresentativeOrderTypes.map((_, tab) => ({
      queryKey: ['getClientOrdersWork', tab + 6],
      queryFn: () =>
        AgentService.getClientOrders(
          tab + 6,
          false,
          999,
          filters.initiators.map((initiator) => initiator.value)
        ),
      onSuccess: (data: AxiosResponse<GetClientOrders>) => {
        setOrdersCount((prevState) => ({
          ...prevState,
          [customerRepresentativeOrderTypes[tab]]: data.data.totalCount,
        }));
      },
    }))
  );

  useEffect(() => {
    const index = Object.values(CustomerRepresentativeOrderTabsEnum).findIndex(
      (key) => CustomerRepresentativeOrderTabsEnum[key] === tab
    );
    queryResults[index].refetch();
  }, [tab, filters]);

  const handleOpenFilterModal = (): void => {
    dispatch(
      modalActions.handleOpenModal({
        content: <CustomerRepresentativeFiltersForm />,
        options: {
          withCloseButton: false,
          modalClassName: 'w-full sm:w-[650px] !overflow-visible',
          contentClassName: '!overflow-visible',
        },
      })
    );
  };

  const isFetching = queryResults.some((query) => query.isFetching);

  const handleRowClick = (tableRow: TableData): void => {
    navigate(`/client/customerrepresentative/orders/${tab}/${tableRow.id}`);
  };

  if (!Object.keys(CustomerRepresentativeOrderTabsEnum).includes(tab))
    return <Navigate to="/client/orders/awaiting" />;

  return (
    <PageWrapper title="Работа с заявками">
      <BalanceInformer />
      <section className="flex flex-col gap-6">
        <Tabs
          tabs={customerRepresentativeTabs}
          ordersCounts={ordersCount}
          isFetching={isFetching}
          activeTab={tab}
          handleClick={() =>
            dispatch(
              ordersActions.setCustomerRepresentativeFilters({
                initiators: [],
              })
            )
          }
        />
        {tab !== CustomerRepresentativeOrderTabsEnum.awaiting && (
          <FilterBlock
            countFilters={determinateCustomerRepresentativeFiltersCount(filters)}
            onFilterButtonClick={handleOpenFilterModal}
          />
        )}
        {queryResults.map((query, index) => {
          if (customerRepresentativeOrderTypes[index] !== tab) {
            return;
          }
          const id = customerRepresentativeOrderTypes[index];
          const { data } = query;

          return (
            <Fragment key={id}>
              {isFetching ? (
                <TableLoading />
              ) : (
                <>
                  {data?.data.totalCount === 0 ? (
                    <div className="min-h-[90vh] overflow-hidden rounded-[10px] bg-base shadow-3xl">
                      <NoneOrders
                        icon={<IconApplication />}
                        heading={
                          determinateCustomerRepresentativeFiltersCount(filters)
                            ? 'Заявки не найдены'
                            : 'В данном разделе нет заявок'
                        }
                        description={
                          determinateCustomerRepresentativeFiltersCount(filters)
                            ? 'Измените настройки фильтра и попробуйте еще раз'
                            : 'Как только заявки появятся, они будут отображаться здесь'
                        }
                      />
                    </div>
                  ) : (
                    <>
                      <TableCard>
                        <Table
                          tableHeading={customerRepresentativeWorkTableHeadings[tab]}
                          tableData={(data?.data.items || []) as unknown as TableData[]}
                          onRowClick={handleRowClick}
                        />
                      </TableCard>
                    </>
                  )}
                </>
              )}
            </Fragment>
          );
        })}
      </section>
    </PageWrapper>
  );
};
